import { Context } from '@front10/helpers/dist/Context';
import { useTranslation } from '@front10/utils';
import dayjs from 'dayjs';
import { useCallback, useContext, useMemo } from 'react';
import { formatDate } from '../../components/controls/DatesRangeSelector/commons';
import {
  fullMonths,
  getWeekDayName,
  weekDaysLong
} from '../../helpers/localization/localizationUtils';
import { reorderWeekdayDependentLanguage, transformBlackoutDaysToDate } from './atoms/helper';
import { useLocalizationData } from './DatePickerLocalization';
import { BlackoutDay } from './types';

export function useWeekDaysLong() {
  const translate = useTranslation();
  const localizationData = useLocalizationData();
  return weekDaysLong(translate, localizationData);
}

export function useDateFormat() {
  const context = useContext(Context);
  return useCallback(
    (day: string | Date, format: string) => {
      return formatDate(dayjs(day).toDate(), format, context);
    },
    [context]
  );
}

export function useWeekDays() {
  const localizationData = useLocalizationData();
  return reorderWeekdayDependentLanguage(localizationData);
}

export function useFullMonths() {
  const translate = useTranslation();
  const localizationData = useLocalizationData();
  return fullMonths(translate, localizationData);
}

export function useWeekDayName() {
  const translate = useTranslation();
  const localizationData = useLocalizationData();
  return useCallback(
    (weekday: number, weekDayFormat: string | null) =>
      getWeekDayName(weekday, weekDayFormat, translate, localizationData),
    [localizationData, translate]
  );
}

export function useBlackoutDates(blackoutDays: BlackoutDay[], blackoutDaysFormat: string) {
  const translate = useTranslation();
  const localizationData = useLocalizationData();
  const context = useMemo(() => ({ translate, localizationData }), [localizationData, translate]);
  return transformBlackoutDaysToDate(blackoutDays, blackoutDaysFormat, context);
}
